import { useMemo } from "react";

import { useBook } from "@/providers/use-book";

export interface BookInsured {
  id: string;
  name: string;
  /**
   * Optional because this type has a second source: `useSearchInsureds` also builds one of these
   * per `search(term)` hit, and `SearchResult` carries no agent. Present exactly for the insureds
   * that came out of a book list, which is the half a scope chip can filter.
   *
   * `agency` is what an agency scope matches on and reads its name off, and is nullable for the
   * same reason `BrokerInsured.agent` is: codegen makes an optional field optional, so no book
   * query forgetting to select it fails to compile.
   */
  agent?: { id: string; firstName: string; lastName: string; agency?: { id: string; name: string } | null };
}

/**
 * Every insured the book has actually loaded, flattened across its lists and deduped by insured id.
 * The same insured routinely lands in several lists — a quote and its opportunity, a renewal and
 * the policy it renews — so the id is the only reliable key.
 */
export const useBookInsureds = (): BookInsured[] => {
  const {
    bindRequested,
    bound,
    brokerReview,
    delivered,
    informationGathering,
    lost,
    pending,
    quoted,
    quotedRenewals,
    solicitedUnsubmittedRenewals,
    submitted,
    submittedRenewals,
    unsolicitedUnsubmittedRenewals,
    unsubmitted,
    upcomingRenewals,
    won,
  } = useBook();

  return useMemo(() => {
    const byId = new Map<string, BookInsured>();

    const lists = [
      bindRequested,
      bound,
      brokerReview,
      delivered,
      informationGathering,
      pending,
      quoted,
      quotedRenewals,
      solicitedUnsubmittedRenewals,
      submitted,
      submittedRenewals,
      unsolicitedUnsubmittedRenewals,
      unsubmitted,
      upcomingRenewals,
      ...Object.values(won),
      ...Object.values(lost),
    ];

    for (const list of lists) {
      for (const { insured } of list) {
        if (!byId.has(insured.id)) {
          const { agent } = insured;

          byId.set(insured.id, {
            id: insured.id,
            name: insured.name,
            // Narrowed to the fields a scope needs rather than spread: the book queries each
            // select their own shape of agent, and passing one through whole would make this type
            // the union of all of them.
            agent: agent
              ? {
                  id: agent.id,
                  firstName: agent.firstName,
                  lastName: agent.lastName,
                  agency: agent.agency ? { id: agent.agency.id, name: agent.agency.name } : null,
                }
              : undefined,
          });
        }
      }
    }

    return [...byId.values()];
  }, [
    bindRequested,
    bound,
    brokerReview,
    delivered,
    informationGathering,
    lost,
    pending,
    quoted,
    quotedRenewals,
    solicitedUnsubmittedRenewals,
    submitted,
    submittedRenewals,
    unsolicitedUnsubmittedRenewals,
    unsubmitted,
    upcomingRenewals,
    won,
  ]);
};
